'use client';

import { useEffect, useMemo, useState } from 'react';
import { Address, formatEther } from 'viem';
import {
  readContract,
  type ReadContractReturnType,
  waitForTransactionReceipt,
  type WaitForTransactionReceiptReturnType,
} from '@wagmi/core';
import { useAccount, useWriteContract } from 'wagmi';
import { CustomToken, TBET_TOKEN } from 'connectors/router';
import { config } from 'index';
import { STACKING_ABI } from 'utils/staking';
import { formatAmountV2 } from 'utils/formatAmount';
import { useAppTranslation } from 'app/hooks';
import terabetLogo from 'assets/images/terabet_coin_logo.png';

interface ClaimVaultProps {
  vaultTitle: string;
  vaultAddress: Address;
}

interface PositionInfo {
  id: number;
  address: string;
  startTime: Date;
  endTime: Date;
  tier: number;
  amount: string;
  reward: string;
  isOpen: boolean;
}

const transformPositonInfo = (res: Array<any>): PositionInfo => {
  return {
    id: res[0],
    address: res[1],
    startTime: new Date(Number(res[2]) * 1000),
    endTime: new Date(Number(res[3]) * 1000),
    tier: Number(res[4]),
    amount: formatEther(res[5]),
    reward: formatEther(res[6]),
    isOpen: res[7],
  };
};

const ClaimVault = (props: ClaimVaultProps) => {
  const t = useAppTranslation();

  const { address } = useAccount();
  const { writeContractAsync } = useWriteContract();
  const [activeCoin, setActiveCoin] = useState<CustomToken>(TBET_TOKEN);
  const [postions, setPositions] = useState<PositionInfo[]>([]);

  useEffect(() => {
    if (address) {
      getPositions();
    }
  }, [address]);

  const getPositionInfo = async (positionId: number): Promise<PositionInfo> => {
    const res: ReadContractReturnType = await readContract(config, {
      address: props.vaultAddress,
      abi: STACKING_ABI.abi,
      functionName: 'position',
      args: [positionId],
      chainId: activeCoin.chainId as any,
    });
    return transformPositonInfo(res as Array<any>);
  };

  const getPositions = async () => {
    const res: ReadContractReturnType = await readContract(config, {
      address: props.vaultAddress,
      abi: STACKING_ABI.abi,
      functionName: 'getPostionByAddress',
      args: [address || '0x'],
      chainId: activeCoin.chainId as any,
    });
    if (res) {
      const arrayOfPositon = String(res).split(',');
      if (arrayOfPositon.length !== 0) {
        const newPostionsInfo: PositionInfo[] = await Promise.all(
          arrayOfPositon.filter((p) => p !== '').map(async (p) => await getPositionInfo(Number(p))),
        );
        setPositions(newPostionsInfo);
      }
    }
  };

  const canClaimItem = useMemo(() => {
    return postions.filter(
      (position) => position.isOpen === true && position.endTime <= new Date(),
    );
  }, [postions, address]);

  const enableClaim = useMemo(() => {
    return canClaimItem.length === 0 || !address;
  }, [canClaimItem, address]);

  const handleClaim = async () => {
    const res = await writeContractAsync({
      chainId: activeCoin.chainId,
      abi: STACKING_ABI.abi,
      address: props.vaultAddress,
      functionName: 'claimAll',
      args: [],
    });
    waitForTransactionReceipt(config, {
      hash: res,
      pollingInterval: 1_000,
    }).then((data: WaitForTransactionReceiptReturnType) => {
      if (data.status === 'success') {
        getPositions();
      }
    });
  };

  const totalStaking = useMemo(() => {
    const countStaking =
      postions.length > 0
        ? postions.reduce((total, postion) => {
            return postion.isOpen ? total + Number(postion.amount) : total;
          }, 0)
        : 0;
    if (canClaimItem.length < 0) {
      return {
        staking: countStaking,
        reward: 0,
      };
    }
    const countReward = canClaimItem.reduce((total, item) => {
      return item.isOpen ? total + Number(item.reward) : total;
    }, 0);
    return {
      staking: countStaking,
      reward: countReward,
    };
  }, [postions, canClaimItem]);

  return (
    <div className="claim-item">
      <div className="claim-item-info">
        <div className="claim-amount">
          <div className="claim-amount__info">
            <p className="amount-title">{t('staking.form.staking')}:</p>
            <p className="amount-number primary-text-color">
              {formatAmountV2(totalStaking.staking, 8)}
              <img className="" src={terabetLogo} alt="terabet_logo" width={20} />
            </p>
          </div>
          <div className="claim-amount__info">
            <p className="amount-title">{t('staking.form.reward')}:</p>
            <p className="amount-number primary-text-color">
              {formatAmountV2(totalStaking.reward, 8)}
              <img className="" src={terabetLogo} alt="terabet_logo" width={20} />
            </p>
          </div>
        </div>
      </div>
      <div className="claim-btn-wrap">
        <span className="fz-sm">{t(props.vaultTitle)}</span>
        <button className="claim-button" onClick={handleClaim} disabled={enableClaim}>
          {t('staking.form.claim')}
        </button>
      </div>
    </div>
  );
};

export default ClaimVault;
